/**
 * React component that is responsible for rendering a CTA section with two halves,
 *  i.e. an image/video on the left or right with text on the other.
 * Copyright (c) 2020 Coursera
 * Author: Kelvin Nguyen
 */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types'
import cx from 'classnames'
import { useMediaQuery } from 'react-responsive'
import Grid from '@material-ui/core/Grid'
import CTAHeader, { CTAHeaderPropTypes } from 'components/CTAHeader'
import SectionWrapper, { SectionWrapperPropTypes } from 'components/SectionWrapper'
import styles from './TwoColumn.module.scss'

const TwoColumnSection = ({
  className,
  backgroundColor,
  flipped,
  lg,
  md,
  children,
  ...props
}) => {
  const isTablet = useMediaQuery({ maxWidth: styles.tablet })
  const Content = useMemo(() => {
    const CallToAction = (
      <Grid
        item
        lg={lg}
        md={md}
        xs={12}>
        <CTAHeader
          className={cx({
            'pr-xl': !isTablet && !flipped,
            'pl-xl': !isTablet && flipped,
            'px-md': isTablet,
          })}
          align="left"
          {...props} />
      </Grid>
    )

    const InnerContent = (
      <Grid item lg={(12 - lg) || false} md={(12 - md) || false} xs={12}>
        {children}
      </Grid>
    )

    return (flipped && !isTablet) ? (
      <>
        {InnerContent}
        {CallToAction}
      </>
    ) : (
      <>
        {CallToAction}
        {InnerContent}
      </>
    )
  }, [md, lg, flipped, children, isTablet, props])

  return (
    <SectionWrapper
      className={cx(className, styles.twoColumnSection, {
        'py-2xl': !className && !isTablet,
        'py-xl': !className && isTablet,
      })}
      backgroundColor={backgroundColor}>
      <Grid
        container
        direction="row"
        justify="center"
        alignItems="center"
        spacing={4}>
        {Content}
      </Grid>
    </SectionWrapper>
  )
}

TwoColumnSection.defaultProps = {
  lg: 6,
  md: 6,
  flipped: false,
}

TwoColumnSection.propTypes = {
  lg: PropTypes.number,
  md: PropTypes.number,
  flipped: PropTypes.bool,
  ...SectionWrapperPropTypes,
  ...CTAHeaderPropTypes,
}

export default TwoColumnSection
